import React, { useState, useEffect } from 'react';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import Map from '../Map/Map';
import useStyles from '../../styles/location';
import useGlobalStyles from '../../styles';
import { GeoJSON } from '../../services/api/api';
import {
  setLocationFloorPlan,
  fetchLocationFloorplan,
  deleteLocationFloorplan,
} from '../../services/apiService';
import {
  setLocationFloorplan,
  setLocationFloorplan as setStateFloorplan,
} from '../../state/actions';
import PositioningMap from '../Map/PositioningMap';
import RemoveDialog from '../Dialog/RemoveDialog';
import { themeProps } from '../../styles/theme';
import { useAppDispatch } from '../../state/store';

interface FloorPlanConfigProps {
  locationId: string;
}

function FloorPlanConfig({ locationId }: FloorPlanConfigProps): JSX.Element {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const { classes: globalClasses } = useGlobalStyles();
  const [file, setFile] = useState<File | undefined>();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(true);
  const [floorMap, setFloorMap] = useState<GeoJSON>();

  useEffect(() => {
    setFloorMap(undefined);
  }, [locationId]);

  useEffect(() => {
    if (file) {
      const r = new FileReader();
      r.onload = (e: ProgressEvent<FileReader>) => {
        const contents = e?.target?.result as string;
        const floorplanJson = contents && JSON.parse(contents);
        const floorPlanType = { type: floorplanJson?.type };
        const floorPlanFeatures = { features: floorplanJson?.features };
        const floorPlan: GeoJSON = { ...floorPlanType, ...floorPlanFeatures };
        setFloorMap(floorPlan);
      };
      r.readAsText(file);
    } else {
      fetchLocationFloorplan(locationId)
        .then((res) => {
          setFloorMap(res);
          dispatch(setLocationFloorplan({ locationId, floorplan: res }));
        })
        .catch(() => null);
    }
  }, [dispatch, file, locationId]);

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFile(undefined);
    closeSnackbar();
    const { files } = event.target;
    const fileExtension = files && files[0].name.split('.').pop();
    const uploadedFile = files && files[0];
    if (
      (uploadedFile !== null && uploadedFile.type === 'application/json') ||
      fileExtension === 'json' ||
      fileExtension === 'geojson'
    ) {
      setFile(uploadedFile as File);
    } else {
      enqueueSnackbar('File is not in correct format', { variant: 'error', persist: true });
    }
  };

  const uploadHandler = () => {
    closeSnackbar();
    if (floorMap) {
      setLocationFloorPlan(locationId, floorMap as GeoJSON)
        .then(() => {
          enqueueSnackbar('Upload successful', { variant: 'success' });
          dispatch(setStateFloorplan({ locationId, floorplan: floorMap }));
          setFile(undefined);
        })
        .catch((err) => enqueueSnackbar(err.cause, { variant: 'error', persist: true }));
    }
  };

  const removeFloorPlan = () => {
    setShowDialog(true);
    closeSnackbar();
    deleteLocationFloorplan(locationId)
      .then(() => {
        enqueueSnackbar('Floorplan has been successfully removed', { variant: 'success' });
        dispatch(
          setStateFloorplan({ locationId, floorplan: { type: 'FeatureCollection', features: [] } })
        );
        setFloorMap(undefined);
        setShowDialog(false);
      })
      .catch((err) => {
        enqueueSnackbar(err.cause, { variant: 'error', persist: true });
        setShowDialog(false);
      });
  };

  return (
    <ListItem>
      <RemoveDialog
        showDialog={showDialog}
        title="floorplan"
        removeHandler={removeFloorPlan}
        cancelHandler={() => setShowDialog(false)}
      />

      <Accordion style={{ width: '100%' }} expanded={expanded}>
        <AccordionSummary
          component="div"
          expandIcon={<ExpandMoreIcon onClick={() => setExpanded(!expanded)} />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <div style={{ width: '100%' }} className={globalClasses.spaceBetweenFlex}>
            <div style={{ display: 'flex' }}>
              <SettingsIcon className={classes.icon} />
              <Typography style={{ alignSelf: 'center', marginLeft: '10px' }}>Floorplan</Typography>
            </div>
            {expanded && (
              <IconButton onClick={() => setIsEditing(!isEditing)} size="large">
                {isEditing ? (
                  <CancelIcon className={globalClasses.closeBtnIcon} />
                ) : (
                  <EditIcon className={classes.icon} data-testid="floorplan-edit" />
                )}
              </IconButton>
            )}
          </div>
        </AccordionSummary>
        <AccordionDetails style={{ padding: '5px' }}>
          <>
            {!floorMap && (
              <Alert severity="error" className={classes.alertMsg}>
                Floor plan not found
              </Alert>
            )}

            {isEditing ? (
              <Box style={{ textAlign: 'center' }}>
                <Typography>Please upload floor plan.</Typography>
                <Typography variant="caption">File must be json or geojson</Typography>
                <div style={{ margin: '1rem 0' }}>
                  <input
                    className={`${classes.uploadInput} form-control`}
                    type="file"
                    name="file"
                    id="choose_file"
                    onChange={(e) => onInputChange(e)}
                    data-testid="choose_file"
                  />
                  <label htmlFor="choose_file">
                    <Button component="span" size="small" variant="outlined">
                      {!file ? 'Choose your file' : file.name}
                    </Button>
                  </label>
                  {floorMap && floorMap.features.length > 0 && !file && (
                    <Button
                      color="secondary"
                      variant={themeProps.btnVariant.default}
                      className={classes.listSensorBtn}
                      onClick={() => setShowDialog(true)}
                    >
                      Remove Floorplan
                    </Button>
                  )}
                </div>
                {floorMap && floorMap.features.length > 0 && (
                  <div className={globalClasses.dashboardTile}>
                    {file && (
                      <Button
                        color="primary"
                        variant={themeProps.btnVariant.default}
                        className={classes.listSensorBtn}
                        onClick={uploadHandler}
                        endIcon={<CloudUploadIcon />}
                      >
                        Upload
                      </Button>
                    )}
                    <PositioningMap
                      floorPlan={floorMap}
                      onChange={() => undefined}
                      position={undefined}
                      mapName="floorplanUpload"
                    />
                  </div>
                )}
              </Box>
            ) : (
              <Map showTaggedSpaces />
            )}
          </>
        </AccordionDetails>
      </Accordion>
    </ListItem>
  );
}

export default FloorPlanConfig;
